import { ordinalNumber } from "utils/helpers"
import { isEqual, isBlank, isPresent } from "utils/helpers"
import { getDateDifference } from "utils/dates"
import { gendersMatch } from "utils/genders_match"
import { toSentence } from "./Attendees"

const gradeText = grade => {
  if (grade === -1) {
    return "Pre-K"
  } else if (grade === 0) {
    return "K"
  } else {
    return ordinalNumber(grade)
  }
}

const isAreaFull = area => isEqual(area.capacityRemaining, 0)

const isGenderRestricted = (area, attendee) => {
  let status = false

  if (isPresent(area.gender) && !gendersMatch(area.gender, attendee.gender)) {
    status = true
  }

  return status
}

const isGradeRestricted = (area, attendee) => {
  let status = false

  if (!isEqual(area.minGrade, "-2") || !isEqual(area.maxGrade, "-2")) {
    if (isBlank(attendee.grade)) {
      status = true
    } else if (
      (isPresent(area, "minGrade") && attendee.grade < area.minGrade) ||
      (isPresent(area, "maxGrade") && attendee.grade > area.maxGrade)
    ) {
      status = true
    }
  }

  return status
}

const isAgeRestricted = (area, attendee) => {
  let status = false

  if (isPresent(area.minAge) || isPresent(area.maxAge)) {
    let attendeeAge = isEqual(area.ageRestrictionUnit, "years") ? attendee.ageToS : attendee.age

    if (isPresent(attendee.birthdate) && isPresent(area.evaluateAgeAt)) {
      attendeeAge = getDateDifference(
        attendee.birthdate,
        area.evaluateAgeAt,
        isEqual(area.ageRestrictionUnit, "years") ? "year" : "month",
        "YYYY-MM-DD"
      )
    }

    if (isBlank(attendeeAge)) {
      status = true
    } else if (
      (isPresent(area.minAge) && attendeeAge < area.minAge) ||
      (isPresent(area.maxAge) && attendeeAge > area.maxAge)
    ) {
      status = true
    }
  }

  return status
}

const isAttendeeTypeRestricted = (area, attendee) => {
  let status = false

  if (
    !Object.values(area.attendeeTypeRestrictions).some(
      a => a.isAvailable && a.name === attendee.type
    )
  ) {
    status = true
  }

  return status
}

export const isAreaAttendeeDisabled = area => isAreaFull(area)

export const genderToString = (gender, defaultValue = null) => {
  if (isEqual(gender, "F")) {
    return "Female"
  } else if (isEqual(gender, "M")) {
    return "Male"
  }

  return defaultValue
}

export const attendeeTypeToString = (hasRestriction, restrictions, defaultValue = null) => {
  if (hasRestriction) {
    return attendeeTypeRestrictionsStrings(restrictions).join(", ")
  }

  return defaultValue
}

function attendeeTypeRestrictionsStrings(restrictions) {
  return restrictions.filter(x => x.isAvailable).map(a => a.name)
}

export const ageToString = (minAge, maxAge, ageUnit, defaultValue = null) => {
  if (minAge && maxAge && isEqual(minAge, maxAge)) {
    return `${minAge} ${ageUnit}`
  } else if (minAge && maxAge && !isEqual(minAge, maxAge)) {
    return `${minAge} - ${maxAge} ${ageUnit}`
  } else if (minAge && !maxAge) {
    return `${minAge} ${ageUnit} and up`
  } else if (maxAge && !minAge) {
    return `${maxAge} ${ageUnit} and under`
  }

  return defaultValue
}

export const gradeToString = (minGrade, maxGrade, defaultValue = null) => {
  if (!isEqual(minGrade, "-2") && isEqual(maxGrade, "-2")) {
    return `${gradeText(minGrade)} and up`
  } else if (!isEqual(maxGrade, "-2") && isEqual(minGrade, "-2")) {
    return `Up to ${gradeText(maxGrade)}`
  } else if (!isEqual(minGrade, "-2") && !isEqual(maxGrade, "-2") && !isEqual(minGrade, maxGrade)) {
    return `${gradeText(minGrade)} - ${gradeText(maxGrade)}`
  } else if (!isEqual(minGrade, "-2") && !isEqual(maxGrade, "-2") && isEqual(minGrade, maxGrade)) {
    return `${gradeText(minGrade)}`
  }

  return defaultValue
}

export function areaAttendeeRestrictionConflictsMessage({ area, attendee }) {
  const conflicts = areaAttendeeRestrictionConflicts(area, attendee)
  if (conflicts.length === 0) return

  const restrictionText = toSentence(conflicts.map(conflict => conflict.allowedText))
  const restrictionActuals = toSentence(
    conflicts.map(conflict => `${conflict.name} ${conflict.actualText}`)
  )
  return `${attendee.firstName}'s ${restrictionActuals}, but this area is restricted to ${restrictionText}.`
}

const NOT_SET = "is not set"

function areaAttendeeRestrictionConflicts(area, attendee) {
  let conflicts = []
  if (isGradeRestricted(area, attendee))
    conflicts.push({
      name: "grade",
      actualText: attendee.gradeToS ? `is set to ${attendee.gradeToS}` : NOT_SET,
      allowedText: `${gradeToString(area.minGrade, area.maxGrade)} grade`
    })
  if (isAgeRestricted(area, attendee))
    conflicts.push({
      name: "age",
      allowed: ageToString(area.minAge, area.maxAge, area.ageRestrictionUnit),
      actual: attendee.ageToS || "unassigned",
      actualText: attendee.age ? `as of ${area.evaluateAgeAt} is ${attendee.ageToS}` : NOT_SET,
      allowedText: `age ${ageToString(area.minAge, area.maxAge, area.ageRestrictionUnit)}`
    })
  if (isGenderRestricted(area, attendee))
    conflicts.push({
      name: "gender",
      actualText: attendee.gender
        ? `is set to ${genderToString(attendee.gender, attendee.gender)}`
        : NOT_SET,
      allowedText: genderToString(area.gender)
    })
  if (isAttendeeTypeRestricted(area, attendee))
    conflicts.push({
      name: "selection",
      actualText: `is ${attendee.type}`,
      allowedText: toSentence(attendeeTypeRestrictionsStrings(area.attendeeTypeRestrictions))
    })

  return conflicts
}

export const areaAttendeeRestrictionToString = (area, attendee) => {
  let status = "Available"

  if (isAreaFull(area)) {
    status = "Full"
  } else if (
    isGradeRestricted(area, attendee) ||
    isAgeRestricted(area, attendee) ||
    isGenderRestricted(area, attendee) ||
    isAttendeeTypeRestricted(area, attendee)
  ) {
    status = "Does not meet restrictions"
  }

  return status
}

export const areaRestrictionsToString = (
  {
    minAge,
    maxAge,
    minGrade,
    maxGrade,
    gender,
    ageRestrictionUnit,
    hasAttendeeTypeRestriction,
    attendeeTypeRestrictions
  },
  signupFeatures = []
) => {
  const grade = gradeToString(minGrade, maxGrade)

  const restrictions = []

  if (signupFeatures.includes("named_attendees")) {
    restrictions.push(
      { label: "Gender", value: genderToString(gender, "Any") },
      {
        label: grade ? "Grade" : "Age",
        value: grade || ageToString(minAge, maxAge, ageRestrictionUnit, "Any")
      }
    )
  }

  restrictions.push({
    label: "Selection",
    value: attendeeTypeToString(hasAttendeeTypeRestriction, attendeeTypeRestrictions, "Any")
  })

  return restrictions
}
